import { useState } from 'react';
import { Plus, MoreVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { UserInlineForm } from '../components/UserInlineForm';
import { useUsers } from '../hooks/useUsers';
import { useUser } from '../hooks/useUser';
import { useAuth } from '@/hooks/useAuth';
import { Permissions } from '@/types';
import { deleteUser } from '../api/users';
import { getApiErrorMessage } from '@/lib/api-error';
import { usePageTitle } from '@/hooks/usePageTitle';
import type { UserDto } from '../types';

export function UsersPage() {
  usePageTitle('Users');
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);

  const { hasPermission } = useAuth();
  const canCreate = hasPermission(Permissions.UsersCreate);
  const canUpdate = hasPermission(Permissions.UsersUpdate);

  const { data: users, isLoading: isLoadingList, error: listError, refetch: refetchList } =
    useUsers(showInactive ? undefined : { isActive: true });

  const { data: selectedUser, isLoading: isLoadingDetail, refetch: refetchDetail } =
    useUser(selectedUserId ?? undefined);

  const handleSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedUserId(value ? parseInt(value) : null);
    setIsAddingNew(false);
  };

  const handleAddNew = () => {
    setSelectedUserId(null);
    setIsAddingNew(true);
  };

  const handleSave = (saved: UserDto) => {
    refetchList();
    setSelectedUserId(saved.id);
    setIsAddingNew(false);
    setPageError(null);
  };

  const handleCancelAdd = () => {
    setIsAddingNew(false);
  };

  const handleDeactivate = async () => {
    if (!selectedUserId) return;
    try {
      await deleteUser(selectedUserId);
      setSelectedUserId(null);
      setPageError(null);
      refetchList();
    } catch (err) {
      setPageError(getApiErrorMessage(err, 'Failed to deactivate user'));
    } finally {
      setIsConfirmOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-background px-5 py-6 space-y-4">
      {/* Header */}
      <div className="bg-card rounded-[10px] p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <h2 className="text-lg font-semibold text-secondary">Users</h2>

          <div className="flex items-center gap-4">
            {/* Record Selector */}
            <div className="w-[300px]">
              <Select
                value={selectedUserId?.toString() ?? ''}
                onChange={handleSelect}
                disabled={isLoadingList}
              >
                <option value="">Select a user...</option>
                {[...users].sort((a, b) => a.fullName.localeCompare(b.fullName)).map((u) => (
                  <option key={u.id} value={u.id.toString()}>
                    {u.fullName}{!u.isActive ? ' (Inactive)' : ''}
                  </option>
                ))}
              </Select>
            </div>

            {/* Show Inactive Toggle */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="showInactive"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              <Label htmlFor="showInactive" className="text-sm font-normal cursor-pointer">
                Include inactive
              </Label>
            </div>

            {/* Add Button */}
            {canCreate && (
              <Button onClick={handleAddNew} className="gap-2">
                <Plus className="h-4 w-4" />
                Add User
              </Button>
            )}

            {/* Actions Menu */}
            {selectedUserId && canUpdate && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white">
                  <DropdownMenuItem
                    onClick={() => setIsConfirmOpen(true)}
                    className="cursor-pointer text-destructive focus:text-destructive"
                  >
                    Deactivate
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>

      <ErrorAlert error={listError} className="rounded-[20px] p-4" />
      <ErrorAlert error={pageError} className="rounded-[20px] p-4" />

      {/* Detail Content */}
      {isLoadingDetail && selectedUserId && (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" label="Loading user..." />
        </div>
      )}

      {isAddingNew && (
        <UserInlineForm
          key="new"
          user={null}
          onSave={handleSave}
          onCancel={handleCancelAdd}
        />
      )}

      {selectedUser && !isAddingNew && (
        <UserInlineForm
          key={selectedUser.id}
          user={selectedUser}
          onSave={(saved) => {
            refetchList();
            refetchDetail();
            setSelectedUserId(saved.id);
          }}
        />
      )}

      {!selectedUserId && !isAddingNew && !isLoadingList && (
        <div className="text-center py-12 text-gray-500">
          Select a user to view details
        </div>
      )}

      {/* Deactivate Confirmation */}
      <ConfirmDialog
        open={isConfirmOpen}
        onOpenChange={setIsConfirmOpen}
        title="Deactivate User"
        message={`Are you sure you want to deactivate "${selectedUser?.fullName}"? This will make the user inactive.`}
        confirmLabel="Deactivate"
        variant="destructive"
        onConfirm={handleDeactivate}
      />
    </div>
  );
}
